import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Search, Filter, FileText, Send, CheckCircle, Clock, AlertCircle, MoreVertical, CreditCard, Copy, ExternalLink, Mail, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusConfig = {
  draft: { label: 'Draft', color: 'bg-slate-500/20 text-slate-400', icon: FileText },
  pending: { label: 'Pending', color: 'bg-yellow-500/20 text-yellow-400', icon: Clock },
  sent: { label: 'Sent', color: 'bg-blue-500/20 text-blue-400', icon: Send },
  paid: { label: 'Paid', color: 'bg-emerald-500/20 text-emerald-400', icon: CheckCircle },
  overdue: { label: 'Overdue', color: 'bg-rose-500/20 text-rose-400', icon: AlertCircle },
  cancelled: { label: 'Cancelled', color: 'bg-gray-500/20 text-gray-400', icon: AlertCircle },
};

const Invoices = () => {
  const navigate = useNavigate();
  const { token, loading: authLoading } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sendingReminder, setSendingReminder] = useState(null);

  useEffect(() => {
    // Wait for auth to finish loading and token to be available
    if (!authLoading && token) {
      fetchInvoices();
    } else if (!authLoading && !token) {
      // No token after auth finished loading - user is not logged in
      setLoading(false);
    }
  }, [token, authLoading]);

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API}/finance/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(response.data);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleSendReminder = async (invoice) => {
    const email = invoice.customer_email || invoice.client_email;
    if (!email) {
      toast.error('Invoice has no customer email address');
      return;
    }
    
    setSendingReminder(invoice.id);
    try {
      const response = await axios.post(
        `${API}/finance/invoices/${invoice.invoice_id || invoice.id}/send-reminder`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.is_overdue) {
        toast.success(`Overdue reminder sent to ${email} (${response.data.days_overdue} days overdue)`);
      } else {
        toast.success(`Payment reminder sent to ${email}`);
      }
    } catch (error) {
      console.error('Error sending reminder:', error);
      toast.error(error.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setSendingReminder(null);
    }
  };

  const handleStatusUpdate = async (invoiceId, newStatus) => {
    try {
      await axios.patch(`${API}/invoices/${invoiceId}/status?status=${newStatus}`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invoice status updated');
      fetchInvoices();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleSubmitEInvoice = async (invoiceId) => {
    try {
      const response = await axios.post(`${API}/invoices/${invoiceId}/submit-einvoice`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchInvoices();
    } catch (error) {
      toast.error('Failed to submit e-invoice');
    }
  };

  const copyPaymentLink = (invoiceId) => {
    const link = `${window.location.origin}/pay/${invoiceId}`;
    navigator.clipboard.writeText(link);
    toast.success('Payment link copied to clipboard!');
  };

  const openPaymentLink = (invoiceId) => {
    window.open(`${window.location.origin}/pay/${invoiceId}`, '_blank');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR'
    }).format(value || 0);
  };

  const filteredInvoices = invoices.filter(inv => 
    inv.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inv.customer_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="invoices-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl sm:text-3xl text-white">Invoices</h1>
          <p className="text-muted-foreground">Manage and track your invoices</p>
        </div>
        <Button 
          className="bg-orbit-glow hover:opacity-90"
          onClick={() => navigate('/suite/finance/invoices/new')}
          data-testid="new-invoice-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Invoice
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-orbit-card border-orbit-border">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search invoices..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-invoices"
              />
            </div>
            <Button variant="outline" className="border-orbit-border">
              <Filter className="w-4 h-4 mr-2" />
              Filter
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Invoices', value: invoices.length, color: 'text-white' },
          { label: 'Pending', value: invoices.filter(i => i.status === 'pending' || i.status === 'sent').length, color: 'text-yellow-400' },
          { label: 'Paid', value: invoices.filter(i => i.status === 'paid').length, color: 'text-emerald-400' },
          { label: 'Overdue', value: invoices.filter(i => i.status === 'overdue').length, color: 'text-rose-400' },
        ].map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Card className="bg-orbit-card border-orbit-border">
              <CardContent className="p-4 text-center">
                <p className="text-sm text-muted-foreground">{stat.label}</p>
                <p className={`text-2xl font-heading font-bold ${stat.color} tabular-nums`}>{stat.value}</p>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Invoices Table */}
      <Card className="bg-orbit-card border-orbit-border">
        <CardHeader>
          <CardTitle className="text-white">All Invoices</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : filteredInvoices.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No invoices found</p>
              <Button 
                className="mt-4 bg-orbit-glow hover:opacity-90"
                onClick={() => navigate('/suite/finance/invoices/new')}
              >
                Create Your First Invoice
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Invoice #</th>
                    <th>Customer</th>
                    <th>Amount</th>
                    <th>Due Date</th>
                    <th>Status</th>
                    <th>E-Invoice</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInvoices.map((invoice) => {
                    const StatusIcon = statusConfig[invoice.status]?.icon || FileText;
                    return (
                      <tr key={invoice.id} className="hover:bg-orbit-card/50">
                        <td className="font-mono text-primary">{invoice.invoice_number}</td>
                        <td>
                          <div>
                            <p className="text-white">{invoice.customer_name}</p>
                            <p className="text-xs text-muted-foreground">{invoice.customer_email}</p>
                          </div>
                        </td>
                        <td className="font-mono text-white">{formatCurrency(invoice.total)}</td>
                        <td className="text-muted-foreground">{invoice.due_date}</td>
                        <td>
                          <Badge className={statusConfig[invoice.status]?.color}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {statusConfig[invoice.status]?.label}
                          </Badge>
                        </td>
                        <td>
                          {invoice.e_invoice_submitted ? (
                            <Badge className="bg-emerald-500/20 text-emerald-400">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Submitted
                            </Badge>
                          ) : (
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-primary hover:text-primary"
                              onClick={() => handleSubmitEInvoice(invoice.id)}
                              data-testid={`submit-einvoice-${invoice.id}`}
                            >
                              Submit to LHDN
                            </Button>
                          )}
                        </td>
                        <td>
                          <div className="flex items-center gap-1">
                            {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => copyPaymentLink(invoice.id)}
                                title="Copy payment link"
                                data-testid={`copy-payment-link-${invoice.id}`}
                              >
                                <CreditCard className="w-4 h-4 mr-1" />
                                <Copy className="w-3 h-3" />
                              </Button>
                            )}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
                                  <>
                                    <DropdownMenuItem 
                                      onClick={() => handleSendReminder(invoice)}
                                      disabled={sendingReminder === invoice.id}
                                    >
                                      {sendingReminder === invoice.id ? (
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                      ) : (
                                        <Mail className="w-4 h-4 mr-2" />
                                      )}
                                      Send Reminder
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => openPaymentLink(invoice.id)}>
                                      <ExternalLink className="w-4 h-4 mr-2" />
                                      Open Payment Page
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => copyPaymentLink(invoice.id)}>
                                      <Copy className="w-4 h-4 mr-2" />
                                      Copy Payment Link
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                  </>
                                )}
                                <DropdownMenuItem onClick={() => handleStatusUpdate(invoice.id, 'sent')}>
                                  Mark as Sent
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleStatusUpdate(invoice.id, 'paid')}>
                                  Mark as Paid
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleStatusUpdate(invoice.id, 'overdue')}>
                                  Mark as Overdue
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleStatusUpdate(invoice.id, 'cancelled')}>
                                  Cancel Invoice
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Invoices;
